/**
 * OrderInsightsPanel.jsx — Sprint P0 (Iter240)
 * =============================================
 * Bloc "Prestation COACHDIA Prism" dans la page commande admin.
 *
 * Pilote :
 *  - Activation COACHDIA Prism (idempotent)
 *  - Envoi des invitations aux collaborateurs
 *  - Configuration & génération du COACHDIA Pulse
 *  - Suivi de complétude (% de tests terminés)
 *
 * Règles métier (validées produit) :
 *  - Génération COACHDIA Pulse éligible si ≥ 80 % ET ≥ 5 profils
 *  - Admin peut forcer la génération manuellement en-dessous du seuil (< 100 %)
 */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkles, Send, Users2, FileCheck2, Loader2, PlayCircle,
  AlertTriangle, CheckCircle2, Wand2,
} from 'lucide-react';
import { getApiUrl } from '../../config/environment';

const API = getApiUrl();

const K_ANONYMITY_MIN = 2;   // 🔧 Sprint 2 — seuil garde abaissé : compatible petites équipes
const COMPLETION_OPTIMAL = 0.8;  // ≥80% → analyse optimale

const STATE_STYLES = {
  not_activated: { label: 'Non activé', color: '#6B7280', bg: '#F3F4F6' },
  awaiting_invitations: { label: 'Prêt à inviter', color: '#92400E', bg: '#FEF3C7' },
  awaiting_completion: { label: 'Tests en cours', color: '#1E40AF', bg: '#DBEAFE' },
  eligible: { label: 'Éligible à la génération', color: '#047857', bg: '#D1FAE5' },
  generated: { label: 'Rapport groupe généré', color: '#065F46', bg: '#A7F3D0' },
  insufficient: { label: 'Seuil non atteint', color: '#991B1B', bg: '#FEE2E2' },
};

export default function OrderInsightsPanel({ orderId, order, token }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const snap = order?.insights_options_snapshot || {};
  const hasIndividual = Boolean(snap.individual_included);
  const hasCollective = Boolean(snap.collective_included);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/enterprise/orders/${orderId}/insights/status`,
        { headers: authHeaders });
      setStatus(r.data);
    } catch (e) {
      // 404 silencieux si order sans insights
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (orderId && (hasIndividual || hasCollective)) loadStatus();
  }, [orderId, hasIndividual, hasCollective]);

  if (!hasIndividual && !hasCollective) {
    return null;
  }

  const activate = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/enterprise/orders/${orderId}/insights/activate`,
        {}, { headers: authHeaders });
      toast.success(r.data.team_report_status === 'prepared'
        ? 'COACHDIA Prism activé — rapport groupe préparé'
        : 'COACHDIA Prism activé');
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Activation impossible');
    } finally { setBusy(false); }
  };

  const sendInvitations = async (subsetIds = null) => {
    setBusy(true);
    try {
      const body = subsetIds && subsetIds.length > 0 ? { collaborator_ids: subsetIds } : {};
      const r = await axios.post(
        `${API}/admin/enterprise/orders/${orderId}/insights/invitations/send`,
        body, { headers: authHeaders });
      toast.success(`Invitations envoyées : ${r.data.sent_count} · Échecs : ${r.data.failed_count}`);
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Envoi impossible');
    } finally { setBusy(false); }
  };

  const configureAndGenerate = async (force = false) => {
    const ids = selectedIds.length > 0
      ? selectedIds
      : (status?.individual?.collaborators || [])
          .filter(c => force || c.insights_consumed_at)
          .map(c => c.id);
    // 🔒 Iter245 — seuil officiel COACHDIA Pulse = 2 profils (aligné K_ANONYMITY_MIN
    // et backend Pydantic min_length=2). Zéro exception.
    if (ids.length < K_ANONYMITY_MIN) {
      toast.error(`Au moins ${K_ANONYMITY_MIN} collaborateurs requis`);
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/enterprise/orders/${orderId}/team-insights/configure`,
        { collaborator_ids: ids, auto_generate: true },
        { headers: authHeaders });
      if (r.data.generated) {
        toast.success(`Rapport COACHDIA Pulse généré · ${r.data.emails_count} profils`);
      } else {
        toast.success(`Configuration sauvegardée — ${r.data.emails_count} profils`);
      }
      if (r.data.partial_warning) {
        toast(`⚠️ Génération partielle : ${r.data.missing_profiles} profil(s) sans test finalisé`);
      }
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Configuration impossible');
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <section className="bg-white border border-[#E8E5DE] rounded-2xl p-5" data-testid="order-insights-loading">
        <div className="flex items-center gap-2 text-sm text-stone-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Chargement de la prestation COACHDIA Prism…
        </div>
      </section>
    );
  }

  const indiv = status?.individual || {};
  const consumed = indiv.consumed_count || 0;
  const total = indiv.total_collaborators || 0;
  const completionPct = indiv.completion_pct || 0;
  const team = status?.collective?.team_report;

  // Calcul de l'état global de la prestation (métier Sprint 2 — seuil 2)
  let globalState = 'not_activated';
  let qualityHint = null;  // 'optimal' | 'partial' | 'minimum'
  if (!status?.insights_activated_at) {
    globalState = 'not_activated';
  } else if (total === 0) {
    globalState = 'awaiting_invitations';
  } else if (hasCollective) {
    if (team?.status === 'generated') {
      globalState = 'generated';
    } else if (consumed >= K_ANONYMITY_MIN) {
      globalState = 'eligible';
      const ratio = consumed / Math.max(total, 1);
      if (ratio >= COMPLETION_OPTIMAL) qualityHint = 'optimal';
      else if (ratio >= 0.5) qualityHint = 'partial';
      else qualityHint = 'minimum';
    } else {
      globalState = 'awaiting_completion';
    }
  } else if (consumed < total) {
    globalState = 'awaiting_completion';
  } else {
    globalState = 'eligible';
  }
  const globalStyle = STATE_STYLES[globalState];

  return (
    <section className="bg-white border border-[#E8E5DE] rounded-2xl p-5" data-testid="order-insights-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#92710A]" />
          <h2 className="text-sm font-display font-medium text-[#111827]">
            Prestation COACHDIA Prism
          </h2>
          <span className="px-2 py-0.5 rounded-full text-[11px] font-medium"
                style={{ color: globalStyle.color, backgroundColor: globalStyle.bg }}
                data-testid="insights-global-state">
            {globalStyle.label}
          </span>
        </div>
        <div className="flex gap-2">
          {!status?.insights_activated_at && (
            <button onClick={activate} disabled={busy}
                    data-testid="insights-activate-btn"
                    className="px-3 py-1.5 bg-[#92710A] text-white rounded-lg text-xs hover:bg-[#78560B] disabled:opacity-60 flex items-center gap-1.5">
              <PlayCircle className="w-3.5 h-3.5" /> Activer COACHDIA Prism
            </button>
          )}
          {status?.insights_activated_at && hasIndividual && total > 0 && (
            <button onClick={() => sendInvitations(null)} disabled={busy}
                    data-testid="insights-invite-all-btn"
                    className="px-3 py-1.5 bg-[#111827] text-white rounded-lg text-xs hover:bg-[#1F2937] disabled:opacity-60 flex items-center gap-1.5">
              <Send className="w-3.5 h-3.5" /> Renvoyer les invitations
            </button>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-4 gap-3 mb-4">
        <MetricTile label="Collaborateurs" value={total} testid="metric-insights-total" />
        <MetricTile label="Invitations envoyées"
                    value={(indiv.collaborators || []).filter(c => c.insights_invitation_sent_at).length}
                    testid="metric-insights-invited" />
        <MetricTile label="Tests terminés" value={consumed}
                    testid="metric-insights-consumed" />
        <MetricTile label="Complétude" value={`${completionPct}%`}
                    accent={completionPct >= COMPLETION_OPTIMAL * 100 ? '#047857' : '#92710A'}
                    testid="metric-insights-completion" />
      </div>

      {total > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between text-xs text-stone-500 mb-1">
            <span>Progression des tests</span>
            <span>{consumed} / {total}</span>
          </div>
          <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
            <div className="h-full bg-[#92710A] transition-all"
                 style={{ width: `${completionPct}%` }} />
          </div>
          <div className="mt-1 text-[10px] text-stone-400">
            Seuil éligibilité COACHDIA Pulse : ≥ {COMPLETION_OPTIMAL * 100}% · ≥ {K_ANONYMITY_MIN} profils
          </div>
        </div>
      )}

      {hasCollective ? (
        <div className="border-t border-stone-100 pt-4 mt-4">
          <div className="flex items-center gap-2 mb-3">
            <Users2 className="w-4 h-4 text-[#92710A]" />
            <h3 className="text-sm font-medium text-[#111827]">COACHDIA Pulse (rapport groupe)</h3>
          </div>

          {team?.status === 'generated' ? (
            <div className="flex items-center gap-2 text-sm p-3 bg-[#D1FAE5] rounded-lg"
                 data-testid="team-insights-generated">
              <CheckCircle2 className="w-4 h-4 text-[#047857]" />
              <span>
                Rapport généré · <strong>{team.team_size}</strong> profils ·
                <a href={`/admin/team-insights/${team.report_id}`}
                   className="text-[#047857] underline ml-1">Consulter</a>
              </span>
            </div>
          ) : consumed < K_ANONYMITY_MIN ? (
            <div className="flex items-start gap-2 text-sm p-3 bg-[#FEF3C7] rounded-lg"
                 data-testid="team-insights-below-min">
              <AlertTriangle className="w-4 h-4 text-[#92400E] mt-0.5" />
              <div>
                Génération indisponible — <strong>{consumed}/{K_ANONYMITY_MIN}</strong> profils finalisés.
                Il faut au minimum <strong>{K_ANONYMITY_MIN}</strong> profils pour générer un rapport groupe.
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {qualityHint === 'optimal' && (
                <div className="flex items-start gap-2 text-sm p-3 bg-[#D1FAE5] rounded-lg"
                     data-testid="team-insights-hint-optimal">
                  <CheckCircle2 className="w-4 h-4 text-[#047857] mt-0.5" />
                  <span><strong>Analyse optimale</strong> — {consumed}/{total} profils complétés (≥80%).</span>
                </div>
              )}
              {qualityHint === 'partial' && (
                <div className="flex items-start gap-2 text-sm p-3 bg-[#FEF3C7] rounded-lg"
                     data-testid="team-insights-hint-partial">
                  <AlertTriangle className="w-4 h-4 text-[#92400E] mt-0.5" />
                  <span><strong>Analyse partielle</strong> — {consumed}/{total} profils seulement. Le rapport sera généré sur les profils disponibles.</span>
                </div>
              )}
              {qualityHint === 'minimum' && (
                <div className="flex items-start gap-2 text-sm p-3 bg-[#FED7AA] rounded-lg"
                     data-testid="team-insights-hint-minimum">
                  <AlertTriangle className="w-4 h-4 text-[#9A3412] mt-0.5" />
                  <span><strong>Analyse au seuil minimum</strong> — {consumed}/{total} profils ({Math.round(consumed/Math.max(total,1)*100)}%). La représentativité sera limitée.</span>
                </div>
              )}
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => configureAndGenerate(false)} disabled={busy}
                        data-testid="team-insights-generate-btn"
                        className="px-3 py-1.5 bg-[#92710A] text-white rounded-lg text-xs hover:bg-[#78560B] disabled:opacity-60 flex items-center gap-1.5">
                  <Wand2 className="w-3.5 h-3.5" /> Générer le rapport groupe
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="border-t border-stone-100 pt-4 mt-4" data-testid="team-insights-not-included">
          <div className="flex items-start gap-2 text-sm p-3 bg-[#F3F4F6] rounded-lg">
            <AlertTriangle className="w-4 h-4 text-[#6B7280] mt-0.5" />
            <div>
              <strong>COACHDIA Pulse non inclus</strong> — cette commande couvre uniquement les analyses individuelles.
              Ajoutez l'option "rapport groupe" au devis pour débloquer la génération collective.
            </div>
          </div>
        </div>
      )}

      {/* 📋 Sprint 2 — Suivi nominatif par collaborateur (pilotage relance) */}
      {total > 0 && (
        <div className="border-t border-stone-100 pt-4 mt-4" data-testid="insights-collab-tracking">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-[#111827] flex items-center gap-2">
              <Users2 className="w-4 h-4 text-[#92710A]" /> Suivi par collaborateur
            </h3>
            <span className="text-[10px] text-stone-400">
              {consumed}/{total} terminés · {(indiv.collaborators || []).filter(c => !c.insights_consumed_at && c.insights_invitation_sent_at).length} à relancer
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-stone-500 border-b border-stone-100">
                  <th className="py-2 pr-2">Collaborateur</th>
                  <th className="py-2 px-2">Invité</th>
                  <th className="py-2 px-2">Activé</th>
                  <th className="py-2 px-2">Test terminé</th>
                  <th className="py-2 px-2">Parcours</th>
                  <th className="py-2 pl-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {(indiv.collaborators || []).map(c => {
                  const invited = Boolean(c.insights_invitation_sent_at);
                  const consumed1 = Boolean(c.insights_consumed_at);
                  const gp = Boolean(c.growth_path_id);
                  return (
                    <tr key={c.id} className="border-b border-stone-50" data-testid={`insights-collab-row-${c.id}`}>
                      <td className="py-2 pr-2">
                        <div className="font-medium text-[#111827]">
                          {(c.first_name || '') + ' ' + (c.last_name || '')}
                        </div>
                        <div className="text-[10px] text-stone-500">{c.email}</div>
                      </td>
                      <td className="py-2 px-2">{invited ? '✓' : '—'}</td>
                      <td className="py-2 px-2">{(c.onboarding_activated_at || c.activated_at) ? '✓' : '—'}</td>
                      <td className="py-2 px-2">{consumed1 ? '✓' : '—'}</td>
                      <td className="py-2 px-2">{gp ? '✓' : '—'}</td>
                      <td className="py-2 pl-2 text-right">
                        {!consumed1 && invited && (
                          <button onClick={() => sendInvitations([c.id])} disabled={busy}
                                  data-testid={`insights-collab-relance-${c.id}`}
                                  className="px-2 py-1 text-[11px] border border-[#92710A] text-[#92710A] rounded-md hover:bg-[#FBF9F3] disabled:opacity-60">
                            Relancer
                          </button>
                        )}
                        {!invited && (
                          <button onClick={() => sendInvitations([c.id])} disabled={busy}
                                  data-testid={`insights-collab-invite-${c.id}`}
                                  className="px-2 py-1 text-[11px] bg-[#111827] text-white rounded-md hover:bg-[#1F2937] disabled:opacity-60">
                            Inviter
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {status?.insights_activated_at && (
        <div className="mt-4 text-[10px] text-stone-400" data-testid="insights-timeline">
          Activé le {new Date(status.insights_activated_at).toLocaleDateString('fr-FR')}
          {team?.generated_at && (<> · Rapport généré le {new Date(team.generated_at).toLocaleDateString('fr-FR')}</>)}
        </div>
      )}
    </section>
  );
}

function MetricTile({ label, value, accent = '#111827', testid }) {
  return (
    <div className="p-3 rounded-xl bg-[#FBF9F3] border border-[#E8DFC8]" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-medium">{label}</div>
      <div className="text-2xl font-display mt-1" style={{ color: accent }}>{value}</div>
    </div>
  );
}
